import { RoutalPalette } from '@nx-smartmonkey/ui';
import tinycolor from 'tinycolor2';

import { getRouteColor, getStopStatusColor } from '../../constants/Colors';
import { STOP_STATUS } from '../../constants/Stops';
import { Driver } from '../../domain/driver/Driver';
import { StopStatus } from '../../domain/stop/StopStatus';

type StopCircleColorProps = {
  color?: string;
  status?: StopStatus;
  number?: number;
  driver?: Driver;
  isPickup?: boolean;
  isSelected?: boolean;
  planDrivers?: Driver[];
  size?: number;
};

const LIGHTEN_AMOUNT = 22;

const StopCircleColor = ({
  color,
  status,
  number,
  driver,
  isPickup,
  isSelected,
  planDrivers,
  size = 24,
}: StopCircleColorProps) => {
  const routeColor = color || (driver && planDrivers ? getRouteColor(driver, planDrivers) : RoutalPalette.neutral20);

  const completedOrCanceledOrIncomplete = status && status !== STOP_STATUS.pending;

  const tBaseColor = completedOrCanceledOrIncomplete ? getStopStatusColor(status, `dark`) : routeColor;

  const getBaseColor = () => {
    if (completedOrCanceledOrIncomplete) {
      return `transparent`;
    }

    if (status === STOP_STATUS.incomplete) {
      return RoutalPalette.markerStopColors.incomplete.medium;
    }
    return tBaseColor;
  };

  const lightenBackgroundColor = completedOrCanceledOrIncomplete
    ? getStopStatusColor(status, `light`)
    : tinycolor(getBaseColor()).lighten(LIGHTEN_AMOUNT).toString();

  const getFillColor = () => (isSelected ? routeColor : lightenBackgroundColor);

  const getFontSize = () => {
    let fontSize = Math.ceil(size / 2);
    if (!number || number <= 99) {
      return fontSize;
    }

    fontSize = Math.ceil(size / 3);
    if (number > 999) {
      fontSize -= 1;
    }
    return fontSize;
  };

  const getContent = () => {
    if (completedOrCanceledOrIncomplete) {
      return (
        <span
          style={{
            position: `absolute`,
            marginTop: `2px`,
            marginLeft: `0px`,
            display: `flex`,
            fontWeight: `bold`,
            fontSize: getFontSize(),
            color: getStopStatusColor(status),
          }}
        >
          {number && number > 999 ? `+999` : number}
        </span>
      );
    }
    if (!number) {
      return null;
    }
    return (
      <span
        style={{
          position: `absolute`,
          fontWeight: `bold`,
          marginTop: `1px`,
          color: `${isSelected ? RoutalPalette.neutral00 : getBaseColor()}`,
          fontSize: getFontSize(),
        }}
      >
        {number > 999 ? `+999` : number}
      </span>
    );
  };

  return (
    <div
      style={{
        position: `relative`,
        width: `${size}px`,
        height: `${size}px`,
        display: `flex`,
        flexDirection: `row`,
        justifyContent: `center`,
        alignItems: `center`,
        borderRadius: `2px`,
      }}
    >
      <div
        style={{
          padding: `0px`,
          width: `100%`,
          height: `100%`,
          position: `relative`,
        }}
      >
        {isPickup ? (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <rect
              x="1.5"
              y="1.5"
              width={`21`}
              height={`21`}
              rx="3.5"
              fill={getFillColor()}
              stroke={getBaseColor()}
              strokeWidth={size > 24 ? `2` : `3`}
            />
          </svg>
        ) : (
          <svg viewBox="0 0 48 48" style={{ position: `relative` }}>
            <circle
              cx="24"
              cy="24"
              r={completedOrCanceledOrIncomplete ? `24` : `21`}
              fill={getFillColor()}
              stroke={getBaseColor()}
              strokeWidth={size > 24 ? `3` : `5`}
            />
          </svg>
        )}
      </div>
      {getContent()}
    </div>
  );
};

export default StopCircleColor;
